import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Particles from '../../components/Particles/Particles';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import slideshowImages from '../../config/slideshowImages';
import './Home.css';

const Home = () => {
    useScrollAnimation();
    const navigate = useNavigate();
    
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    
    useEffect(() => {
        console.log('🖼️ Slideshow initialized with', slideshowImages.length, 'images');
        console.log('📋 All image paths:', slideshowImages);
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) => {
                const nextIndex = prevIndex === slideshowImages.length - 1 ? 0 : prevIndex + 1;
                console.log('🔄 Changing slide from', prevIndex, 'to', nextIndex);
                console.log('📸 New image URL:', slideshowImages[nextIndex]);
                return nextIndex;
            });
        }, 5000); // Change image every 5 seconds
        
        return () => {
            console.log('Slideshow cleanup');
            clearInterval(interval);
        };
    }, []); // Run once on mount
    
    // Log current image whenever it changes
    useEffect(() => {
        console.log('✅ CURRENT ACTIVE IMAGE:', slideshowImages[currentImageIndex]);
        console.log('📍 Current index:', currentImageIndex);
    }, [currentImageIndex]);
    
    return (
        <div className="home-page">
            {/* Hero Section with Auto-changing Images */}
            <section className="hero">
                {/* Background Image Slider */}
                <div className="hero-slider">
                    {slideshowImages.map((image, index) => (
                        <div
                            key={index}
                            className={`hero-slide ${index === currentImageIndex ? 'active' : ''}`}
                        >
                            <img 
                                src={image} 
                                alt={`Slide ${index + 1}`}
                                onLoad={() => console.log('✅ Image loaded:', image)}
                                onError={() => console.error('❌ Image FAILED to load:', image)}
                            />
                        </div>
                    ))}
                </div>
                
                <Particles />
                <div className="hero-overlay"></div>
                
                <div className="hero-content">
                    <h1 className="hero-title">Together We Can Change Lives</h1>
                    <p className="hero-subtitle">Join us in making a positive impact on communities around the world</p>
                    <div className="hero-buttons">
                        <button onClick={() => navigate('/about')} className="btn btn-secondary">Learn More</button>
                    </div>
                </div>
                
                {/* Slider Indicators */}
                <div className="hero-indicators">
                    {slideshowImages.map((_, index) => (
                        <button
                            key={index}
                            className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                            onClick={() => setCurrentImageIndex(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
                
                <div className="scroll-indicator">
                    <i className="fas fa-chevron-down"></i>
                </div>
            </section>

            {/* Statistics Section */}
            <section className="stats">
                <div className="container">
                    <div className="stat-item scroll-animate">
                        <div className="stat-number">15+</div>
                        <div className="stat-label"><i className="fa-solid fa-list-check"></i> Active Projects</div>
                    </div>
                    <div className="stat-item scroll-animate">
                        <div className="stat-number">100+</div>
                        <div className="stat-label"><i className="fa-solid fa-people-group"></i> Volunteers</div>
                    </div>
                    <div className="stat-item scroll-animate">
                        <div className="stat-number">18+</div>
                        <div className="stat-label" > <i className="fa-solid fa-location-dot"></i> Districts</div>
                    </div>
                </div>
            </section>

            {/* About Preview */}
            <section className="about-preview">
                <div className="container">
                    <div className="section-header">
                        <span className="section-tag">Who We Are</span>
                        <h2 className="section-title">About Our Mission</h2>
                        <p className="section-description">Empowering communities through education, healthcare, and sustainable development</p>
                    </div>
                    <div className="about-content">
                        <div className="about-text scroll-animate">
                            <h3>Building a Better Tomorrow</h3>
                            <p>Nidharsanam Public Charitable Trust is dedicated to creating sustainable change in underserved communities worldwide. Through our innovative programs and dedicated volunteers, we focus on education, healthcare, environmental conservation, and economic empowerment.</p>
                            <button onClick={() => navigate('/about')} className="btn btn-primary">Learn More</button>
                        </div>
                        <div className="team-preview scroll-animate-scale">
                            <div className="team-grid">
                                <div className="team-member-card">
                                    <div className="team-member-image">
                                        <img src={`${process.env.PUBLIC_URL}/images/team/sai.jpeg`} alt="Karunamoorthy" />
                                    </div>
                                    <h4>Dr. M.N. Karunamoorthy (a) Sai Krishnan</h4>
                                    <p className="team-role">Founder & Managing Trustee</p>
                                    <p className="team-org">Nidharsanam Public Charitable Trust</p>
                                </div>
                                <div className="team-member-card">
                                    <div className="team-member-image">
                                        <img src={`${process.env.PUBLIC_URL}/images/team/karthik.jpg`} alt="Kartick" />
                                    </div>
                                    <h4>Kartick</h4>
                                    <p className="team-role">CEO</p>
                                    <p className="team-org">Fervid Pro IT Solutions Pvt.Ltd</p>
                                </div>
                                <div className="team-member-card">
                                    <div className="team-member-image">
                                        <img src={`${process.env.PUBLIC_URL}/images/team/aadhi.jpeg`} alt="Aadithyan Ramesh" />
                                    </div>
                                    <h4>Aadithyan Ramesh</h4>
                                    <p className="team-role">Director Film Industry</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What We Do Section */}
            <section className="what-we-do-section">
                <div className="container">
                    <div className="section-header">
                        <h2 className="section-title">WHAT WE DO</h2>
                    </div>
                    
                    <div className="info-grid">
                        <div className="info-item scroll-animate" onClick={() => navigate('/programs')}>
                            <div className="item-box">
                                <img src={`${process.env.PUBLIC_URL}/NIDLOGO.jpeg`} alt="Logo" />
                            </div>
                            <h3>கல்வி (Education)</h3>
                        </div>

                        <div className="info-item scroll-animate" onClick={() => navigate('/programs')}>
                            <div className="item-box blue"></div>
                            <h3>அன்னதானம் (Annadhanam)</h3>
                        </div>

                        <div className="info-item scroll-animate" onClick={() => navigate('/programs')}>
                            <div className="item-box blue"></div>
                            <h3>AI தொழில்நுட்பம் (AI Technology)</h3>
                        </div>
                    </div>
                </div> 
            </section>
        </div>
    );
};

export default Home;
